<template>
  <div class="table-wrap">
    <div class="table-title-div">
      <p class="table-title">Well & Sampling Information</p>
    </div>
    
    <div class="tables-container">
      <!-- Well Information Table -->
      <div class="table-section">
        <!-- Skeleton loader while loading -->
        <div v-if="loading" class="skeleton-loader">
          <div class="skeleton-header"></div>
          <div v-for="i in 6" :key="i" class="skeleton-row">
            <div class="skeleton-cell"></div>
            <div class="skeleton-cell"></div>
          </div>
        </div>
        
        <v-data-table 
          v-else-if="wellInfoItems.length > 0" 
          :headers="wellInfoHeaders"
          :items="wellInfoItems"
          :loading="false" 
          :items-per-page="-1"
          fixed-header 
          max-height="calc(100vh - 420px)"
          class="sample-info-table">
          <template v-slot:headers>
            <tr class="main-header">
              <th class="sticky-header">Well Information</th>
              <th class="sticky-header">Value</th>
            </tr>
          </template>
          <template v-slot:body="{ items }">
            <tr v-for="(item, index) in items" :key="index">
              <td>{{ item.propertyName }}</td>
              <td>
                <v-tooltip location="top" v-if="typeof item.value === 'number'">
                  <template v-slot:activator="{ props }">
                    <span v-bind="props" class="value-text">
                      {{ item.digits > 0 ? item.value.toFixed(item.digits) : item.value }}
                    </span>
                  </template>
                  <span>{{ item.value }}</span>
                </v-tooltip>
                <span v-else class="value-text">{{ item.value || '-' }}</span>
                <span v-if="item.unit" class="uom">&nbsp;{{ item.unit }}</span>
              </td>
            </tr>
          </template>
        </v-data-table>
        <div v-else class="no-data-message">
          <h4>No well information available for this sample.</h4>
        </div>
      </div>

      <!-- Sampling Information Table -->
      <div class="table-section">
        <!-- Skeleton loader while loading -->
        <div v-if="loading" class="skeleton-loader">
          <div class="skeleton-header"></div>
          <div v-for="i in 6" :key="i" class="skeleton-row">
            <div class="skeleton-cell"></div>
            <div class="skeleton-cell"></div>
          </div>
        </div>
        
        <v-data-table 
          v-else-if="samplingInfoItems.length > 0" 
          :headers="samplingInfoHeaders"
          :items="samplingInfoItems"
          :loading="false" 
          :items-per-page="-1"
          fixed-header 
          max-height="calc(100vh - 420px)"
          class="sample-info-table">
          <template v-slot:headers>
            <tr class="main-header">
              <th class="sticky-header">Sampling Information</th>
              <th class="sticky-header">Value</th>
            </tr>
          </template>
          <template v-slot:body="{ items }">
            <tr v-for="(item, index) in items" :key="index">
              <td>{{ item.propertyName }}</td>
              <td>
                <v-tooltip location="top" v-if="typeof item.value === 'number'">
                  <template v-slot:activator="{ props }">
                    <span v-bind="props" class="value-text">
                      {{ item.digits > 0 ? item.value.toFixed(item.digits) : item.value }}
                    </span>
                  </template>
                  <span>{{ item.value }}</span>
                </v-tooltip>
                <span v-else class="value-text">{{ item.value || '-' }}</span>
                <span v-if="item.unit" class="uom">&nbsp;{{ item.unit }}</span>
              </td>
            </tr>
          </template>
        </v-data-table>
        <div v-else class="no-data-message">
          <h4>No sampling information available for this sample.</h4>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../../../axiosInterceptor";
import { getDisplayName, getSortOrder } from '../../../../utils/cache';

export default {
  name: "WellInfoTab",
  data: () => ({
    loading: true,
    wellInfoHeaders: [],
    wellInfoItems: [],
    samplingInfoHeaders: [],
    samplingInfoItems: [],
    sampleData: null,
    wellInfoSortOrder: [],
    samplingInfoSortOrder: [],
    documentDownload: require("../../../../assets/Images/csv.svg")
  }),
  methods: {
    async fetchSampleData() {
      try {
        const sampleId = this.$route.params.sampleId;
        if (sampleId) {
          const response = await api.get(`/public/api/samples/${sampleId}`);
          if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
            this.sampleData = response.data.data;
            await this.processWellInfoData();
            await this.processSamplingInfoData();
            await this.processHeaderData();
          }
        }
      } catch (error) {
        console.error('Error fetching sample data:', error);
      }
    },

    async processHeaderData() {
      try {
        const headerSortOrder = await getSortOrder("sample", "sample_well_information", "header");
        
        if (this.sampleData && this.sampleData.pageData && this.sampleData.pageData.wellInformationHeader) {
          const headerData = this.sampleData.pageData.wellInformationHeader;
          const headers = [];
          
          if (Array.isArray(headerData)) {
            const sortedHeader = [...headerData].sort((a, b) => {
              const indexA = headerSortOrder.indexOf(a.key);
              const indexB = headerSortOrder.indexOf(b.key);
              return indexA - indexB;
            });

            for (const item of sortedHeader) {
              const { key, value } = item;
              
              const camelCaseKey = key
                .split(' ')
                .map((word, index) => 
                  index === 0 
                    ? word.toLowerCase() 
                    : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                )
                .join('');

              let displayName, digits;
              try {
                const res = await getDisplayName('sample', camelCaseKey);
                displayName = res.displayName || key;
                digits = res.digits != null ? res.digits : 0;
              } catch {
                displayName = key;
                digits = 0;
              }

              headers.push({
                displayName: displayName,
                key: camelCaseKey,
                value: value,
                digits: digits,
                sortOrder: headers.length + 1
              });
            }
          }

          this.$emit('data-updated', {
            headers: headers,
            testID: this.sampleData?.ID || ""
          });
        } else {
          const fallbackHeaders = [
            { displayName: "Sample ID", key: "sampleId", value: this.sampleData?.fluidSampleID || this.sampleData?.ID, digits: 0, sortOrder: 1 },
            { displayName: "Asset", key: "asset", value: this.sampleData?.location?.asset, digits: 0, sortOrder: 2 },
            { displayName: "Field", key: "field", value: this.sampleData?.location?.field, digits: 0, sortOrder: 3 },
            { displayName: "Well", key: "well", value: this.sampleData?.location?.well, digits: 0, sortOrder: 4 }
          ];

          this.$emit('data-updated', {
            headers: fallbackHeaders,
            testID: this.sampleData?.ID || ""
          });
        }
      } catch (error) {
        console.error('Error processing header data:', error);
        const fallbackHeaders = [
          { displayName: "Sample ID", key: "sampleId", value: this.sampleData?.fluidSampleID || this.sampleData?.ID, digits: 0, sortOrder: 1 },
          { displayName: "Asset", key: "asset", value: this.sampleData?.location?.asset, digits: 0, sortOrder: 2 },
          { displayName: "Field", key: "field", value: this.sampleData?.location?.field, digits: 0, sortOrder: 3 },
          { displayName: "Well", key: "well", value: this.sampleData?.location?.well, digits: 0, sortOrder: 4 }
        ];

        this.$emit('data-updated', {
          headers: fallbackHeaders,
          testID: this.sampleData?.ID || ""
        });
      }
    },

    async processWellInfoData() {
      if (!this.sampleData) {
        return;
      }

      try {
        this.wellInfoSortOrder = await getSortOrder("sample", "sample_well_information", "table");

        this.wellInfoHeaders = [
          {
            title: 'Key',
            key: 'propertyName',
            align: 'start',
            sortable: false
          },
          {
            title: 'Value',
            key: 'value',
            align: 'start',
            sortable: false
          }
        ];

        this.wellInfoItems = [];

        const dataSource = this.sampleData.sample;
        const locationSource = this.sampleData.sample.location || {};
        
        // Only include keys that are in the sort order
        const availableKeys = this.wellInfoSortOrder.filter(key => {
          // Check if key exists in sample data
          const inSample = dataSource.hasOwnProperty(key) && 
            key !== 'sampleType' && 
            key !== 'ID' && 
            key !== 'location' && 
            key !== 'pageData' && 
            !key.endsWith('_UOM');
          
          // Check if key exists in location data
          const inLocation = locationSource.hasOwnProperty(key);
          
          return inSample || inLocation;
        });

        const sortedKeys = [...availableKeys].sort((a, b) => {
          const indexA = this.wellInfoSortOrder.indexOf(a);
          const indexB = this.wellInfoSortOrder.indexOf(b);
          return indexA - indexB;
        });

        let rowOrder = 1;
        for (const key of sortedKeys) {
          // Get value from sample data first, then from location data
          let value = dataSource[key];
          let dataSourceForUnit = dataSource;
          
          if (value === null || value === undefined) {
            value = locationSource[key];
            dataSourceForUnit = locationSource;
          }
          
          if (value !== null && value !== undefined && !Array.isArray(value) && typeof value !== 'object') {
            let displayName, digits;
            try {
              const res = await getDisplayName('sample', key);
              displayName = res.displayName || this.formatHeaderName(key);
              digits = res.digits != null ? res.digits : 0;
            } catch {
              displayName = this.formatHeaderName(key);
              digits = 0;
            }

            const tableItem = {
              propertyName: displayName,
              value: value,
              unit: this.getUnitForProperty(key, dataSourceForUnit),
              digits: digits,
              originalKey: key,
              rowOrder: rowOrder
            };
            this.wellInfoItems.push(tableItem);
            rowOrder++;
          }
        }

        this.loading = false;
      } catch (error) {
        console.error('Error processing well info data:', error);
        this.loading = false;
      }
    },

    async processSamplingInfoData() {
      if (!this.sampleData) {
        return;
      }

      try {
        this.samplingInfoSortOrder = await getSortOrder("sample", "sample_sampling_information", "table");

        this.samplingInfoHeaders = [
          {
            title: 'Key',
            key: 'propertyName',
            align: 'start',
            sortable: false
          },
          {
            title: 'Value',
            key: 'value',
            align: 'start',
            sortable: false
          }
        ];

        this.samplingInfoItems = [];

        const dataSource = this.sampleData.sample;
        const locationSource = this.sampleData.samplelocation || {};
        
        // Only include keys that are in the sort order
        const availableKeys = this.samplingInfoSortOrder.filter(key => {
          // Check if key exists in sample data
          const inSample = dataSource.hasOwnProperty(key) && 
            key !== 'sampleType' && 
            key !== 'ID' && 
            key !== 'location' && 
            key !== 'pageData' && 
            !key.endsWith('_UOM');
          
          // Check if key exists in location data
          const inLocation = locationSource.hasOwnProperty(key);
          
          return inSample || inLocation;
        });

        const sortedKeys = [...availableKeys].sort((a, b) => {
          const indexA = this.samplingInfoSortOrder.indexOf(a);
          const indexB = this.samplingInfoSortOrder.indexOf(b);
          return indexA - indexB;
        });

        let rowOrder = 1;
        for (const key of sortedKeys) {
          // Get value from sample data first, then from location data
          let value = dataSource[key];
          let dataSourceForUnit = dataSource;
          
          if (value === null || value === undefined) {
            value = locationSource[key];
            dataSourceForUnit = locationSource;
          }
          
          if (value !== null && value !== undefined && !Array.isArray(value) && typeof value !== 'object') {
            let displayName, digits;
            try {
              const res = await getDisplayName('sample', key);
              displayName = res.displayName || this.formatHeaderName(key);
              digits = res.digits != null ? res.digits : 0;
            } catch {
              displayName = this.formatHeaderName(key);
              digits = 0;
            }

            const tableItem = {
              propertyName: displayName,
              value: value,
              unit: this.getUnitForProperty(key, dataSourceForUnit),
              digits: digits,
              originalKey: key,
              rowOrder: rowOrder
            };
            this.samplingInfoItems.push(tableItem);
            rowOrder++;
          }
        }

        this.loading = false;
      } catch (error) {
        console.error('Error processing sampling info data:', error);
        this.loading = false;
      }
    },

    formatHeaderName(key) {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    },

    getUnitForProperty(key, dataSource) {
      // First try to get unit from the data source using _UOM suffix
      const uomKey = key + '_UOM';
      if (dataSource && dataSource[uomKey]) {
        return dataSource[uomKey];
      }
      
      // If no UOM field found, return empty string
      return "";
    },

    downloadWellInfoCSV() {
      if (this.wellInfoItems && this.wellInfoItems.length > 0) {
        const fileName = `well_information_table.csv`;
        const csvHeader = this.wellInfoHeaders.map((header) => header.title);
        const csvRows = this.wellInfoItems.map((item) => {
          let formattedValue = item.value;
          
          // Format numbers with proper decimal places
          if (typeof item.value === 'number') {
            formattedValue = item.digits > 0 ? item.value.toFixed(item.digits) : item.value;
          } else if (item.value === null || item.value === undefined) {
            formattedValue = '-';
          }
          
          // Add unit if available
          if (item.unit) {
            formattedValue = `${formattedValue} ${item.unit}`;
          }
          
          return [item.propertyName, formattedValue];
        });
        const csvContent = [
          csvHeader.join(","),
          ...csvRows.map((row) => row.join(",")),
        ].join("\n");
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          console.error("Your browser does not support downloading files programmatically. Please try a different browser.");
        }
      } else {
        console.error("No data available in the well information table to download.");
      }
    },

    downloadSamplingInfoCSV() {
      if (this.samplingInfoItems && this.samplingInfoItems.length > 0) {
        const fileName = `sampling_information_table.csv`;
        const csvHeader = this.samplingInfoHeaders.map((header) => header.title);
        const csvRows = this.samplingInfoItems.map((item) => {
          let formattedValue = item.value;
          
          // Format numbers with proper decimal places
          if (typeof item.value === 'number') {
            formattedValue = item.digits > 0 ? item.value.toFixed(item.digits) : item.value;
          } else if (item.value === null || item.value === undefined) {
            formattedValue = '-';
          }
          
          // Add unit if available
          if (item.unit) {
            formattedValue = `${formattedValue} ${item.unit}`;
          }
          
          return [item.propertyName, formattedValue];
        });
        const csvContent = [
          csvHeader.join(","),
          ...csvRows.map((row) => row.join(",")),
        ].join("\n");
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          console.error("Your browser does not support downloading files programmatically. Please try a different browser.");
        }
      } else {
        console.error("No data available in the sampling information table to download.");
      }
    }
  },
  mounted() {
    this.fetchSampleData();
  }
};
</script>

<style scoped>
.tables-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.pointer {
  cursor: pointer;
}

.no-data-message {
  text-align: center;
  padding: 40px;
  color: #666;
}

.no-data-message h4 {
  margin: 0;
  font-size: 15.50px;
  font-weight: 500;
}

/* Skeleton Loader Styles */
.skeleton-loader {
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.skeleton-header {
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 12px;
}

.skeleton-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 8px;
}

.skeleton-cell {
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Responsive design for smaller screens */
@media (max-width: 1200px) {
  .tables-container {
    grid-template-columns: 1fr;
  }
}
</style>
